// "How a SAFed day works" (Session BDB): the short engineer guide, built from the same rule book the gate sheet, the restoration list
// and the failure path use. Plain English, under one page on a phone. The failure steps and the gate and restoration headings are
// held equal to the server's own lists by server/src/modules/safed/safedHowToParity.test.ts, so this page cannot drift from the rules.
export interface HowToSection {
  heading: string;
  lines: string[];
}

/** Word for word the server's FAILURE_STEPS (safedWorkflow.ts). */
export const HOWTO_FAILURE_STEPS: readonly string[] = [
  "Stop. Do not re-run the test, and do not reset or release the device.",
  "Make the area and the lift safe.",
  "Phone the Director.",
  "Photograph everything as found.",
  "Record facts only. Do not state a cause, a fault or blame.",
  "The lift does not return to service until it is repaired and the test has been repeated and passed.",
];

export const HOWTO_SECTIONS: readonly HowToSection[] = [
  {
    heading: "1. Before you start",
    lines: [
      "The gate screen must be complete before the first test. Every item is ticked.",
      "If the test needs the car top or the pit, answer the access question and work through the Access, LR1 Issue 04 items. Each points to the LR1 section to read.",
      "ELE rule: a stress test (our own risk label, not an LG1 term) cannot start until three things are true: the customer's signed authorisation is recorded, two engineers are on the job, and you are signed off for it.",
      "If the screen says no engineer is signed off for this test, stop and tell the office. Only the office can switch that on.",
      "The lift is out of service, the notices are up, the weights are on site if needed, and the building contact is with you.",
    ],
  },
  {
    heading: "2. One test at a time",
    lines: [
      "Each test has its own screen. Open Method if you need it. Do not work from memory.",
      "Each test screen is the printed LG1 Annex A form. Yes means satisfactory, except on the two questions that ask for a finding, where a Yes needs its explanation. A No needs a note.",
      "Write down the values the screen asks for, with their units.",
      "Every test ends with two questions: is further remedial work necessary, and if so what you recommend. That is a recorded result, not a failed test.",
    ],
  },
  {
    heading: "3. Put the lift back",
    lines: [
      "ELE rule: after a safety gear test, work through the restoration list: rails dressed, parts replaced, gear reset, car free-running, weights counted, notices removed.",
      "The job cannot be sent to the office until the list is done.",
    ],
  },
  {
    heading: "4. If something does not work",
    lines: [...HOWTO_FAILURE_STEPS],
  },
  {
    heading: "5. Photographs",
    lines: [
      "You can add up to six photographs to the visit. They are optional and never stop a test being completed.",
      "On a failure, take the damage photographs first.",
      "Photographs can be taken with no signal. They are sent when you are back in range.",
    ],
  },
];
